import React, { ReactNode, useEffect, useState } from 'react';
import { Center, Flex, FlexProps, MantineSize, Text } from '@mantine/core';
import { useClickOutside, useHover } from '@mantine/hooks';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';

type EditableNameProps = FlexProps & {
  name: string;
  onSave: (newName: string) => Promise<void> | void; // your API call
  onLooseFocus?: () => void;
  editing?: boolean;
  saving: boolean;
  fw?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  lineClamp?: number;
  inputType?: 'textinput' | 'textarea';
  placeholder?: string;
  editingDisabled?: boolean;
  maxLength?: number;
  leftSection?: ReactNode;
  leftSectionWidth?: string | number;
  textInputHeight?: string;
};

const sizeStyles = {
  fontSize: { sm: '13px', md: '14px', lg: '18px', xl: '24px' },
  // lineHeight: { sm: '33px', md: '39px', lg: '47px', xl: '57px' },
  height: { sm: '36px', md: '42px', lg: '50px', xl: '60px' },
  paddingX: { sm: '12px', md: '14px', lg: '16.67px', xl: '20px' },
  paddingY: { sm: '5.5px', md: '7px', lg: '10px', xl: '12px' },
  loaderSize: { sm: 'xs', md: 'sm', lg: 'md', xl: 'lg' },
  // leftSectionWidth: { sm: '34px', md: '40px', lg: '48px', xl: '58px' },
};

export function EditableText({
  name,
  onSave,
  onLooseFocus,
  editing = false,
  saving,
  fw = 500,
  size = 'md',
  lineClamp = 1,
  inputType = 'textinput',
  placeholder,
  editingDisabled = false,
  maxLength = 255,
  leftSection,
  leftSectionWidth = '30px',
  textInputHeight,
  ...props
}: EditableNameProps) {
  const [localEditing, setLocalEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [originalValue, setOriginalValue] = useState(name);

  // keep local value in sync if parent name changes while not editing
  useEffect(() => {
    if (!editing && !saving && !localEditing) {
      setValue(name);
      setOriginalValue(name);
    }
  }, [name, editing, saving, localEditing]);

  const startEditing = () => {
    setOriginalValue(name); // capture original when edit starts
    setValue(name);
    setLocalEditing(true);
  };

  const handleSaveTextInput = async (newValue: string) => {
    if (!newValue) {
      setValue(originalValue);
    } else if (newValue !== originalValue) {
      await onSave(newValue);
    }
  };

  const handleSaveTextArea = async (newValue: string) => {
    if (newValue !== originalValue) {
      await onSave(newValue);
    }
  };

  const handleSave = async (cancelled: boolean) => {
    if (saving) return;
    if (inputType === 'textinput') {
      await handleSaveTextInput(cancelled ? originalValue : value.trim());
    } else {
      await handleSaveTextArea(value);
    }
    onLooseFocus?.();
    setLocalEditing(false);
  };

  const clickOutsideRef = useClickOutside(() => handleSave(false));

  const isTextArea = inputType === 'textarea';
  const isTextInput = inputType === 'textinput';

  const Component = isTextArea ? StyledTextArea : StyledTextInput;

  if (!editing && !localEditing) {
    return (
      <>
        <DisplayName
          name={name}
          fw={fw}
          size={size}
          onEdit={startEditing}
          lineClamp={lineClamp}
          saving={saving}
          placeholder={placeholder}
          inputType={inputType}
          editingDisabled={editingDisabled}
          leftSection={leftSection}
          leftSectionWidth={leftSectionWidth}
          textInputHeight={textInputHeight}
          {...props}
        />
      </>
    );
  }

  return (
    <Flex ref={clickOutsideRef} w={'100%'} {...props}>
      <Component
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            await handleSave(true);
          } else if (e.key === 'Enter' && isTextInput) {
            // Only commit on Enter for text inputs (textarea keeps newline behavior)
            e.preventDefault();
            await handleSave(false);
          }
        }}
        autoFocus
        required
        styles={{
          input: {
            border: '1px solid var(--velos-brand-blue)',
            fontWeight: fw,
            fontSize: sizeStyles.fontSize[size],
            paddingLeft: leftSection ? leftSectionWidth : undefined,
            ...(textInputHeight && isTextInput
              ? { height: textInputHeight, minHeight: textInputHeight }
              : {}),
          },
          section: { width: leftSectionWidth },
        }}
        w={'100%'}
        size={size}
        leftSection={leftSection}
        {...(isTextArea ? { minRows: 1, autosize: true, maxRows: lineClamp } : {})}
      />
    </Flex>
  );
}

type DisplayNameProps = FlexProps & {
  name: string;
  fw?: number;
  size: 'sm' | 'md' | 'lg' | 'xl';
  onEdit?: () => void;
  lineClamp: number;
  saving: boolean;
  placeholder?: string;
  inputType?: 'textinput' | 'textarea';
  editingDisabled: boolean;
  leftSection: ReactNode;
  leftSectionWidth: string | number;
  textInputHeight?: string;
};

function DisplayName({
  name,
  fw,
  size,
  onEdit,
  lineClamp,
  saving,
  placeholder,
  inputType,
  editingDisabled,
  leftSection,
  leftSectionWidth,
  textInputHeight,
  ...props
}: DisplayNameProps) {
  const isTextArea = inputType === 'textarea';
  const isTextInput = inputType === 'textinput';

  const { hovered, ref } = useHover();
  const height = sizeStyles.height[size];
  const fontSize = sizeStyles.fontSize[size];
  const paddingX = sizeStyles.paddingX[size];
  const paddingY = isTextArea ? sizeStyles.paddingY[size] : undefined;
  const loaderSize = sizeStyles.loaderSize[size] as MantineSize;
  const textPaddingTop = isTextInput ? '1px' : undefined;

  return (
    <Flex
      ref={ref}
      {...props}
      style={{
        ...props?.style,
        borderColor: hovered && !editingDisabled ? '#ECECED' : 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '8px',
        flex: 1,
        minWidth: 0,
      }}
      align="center"
      px={paddingX}
      {...(textInputHeight && isTextInput
        ? { mih: textInputHeight, mah: textInputHeight }
        : { py: paddingY, mih: height })}
      // py={paddingY}
      // mih={height}
      // mih={'30px'}
      // mah={'30px'}
      pos="relative"
      w={'100%'}
      onClick={!editingDisabled ? onEdit : undefined}
    >
      <CustomLoadingOverlay visible={saving} loaderSize={loaderSize} />

      {leftSection && (
        <Center miw={leftSectionWidth} ml={`calc(-0.5px - ${paddingX})`} mr={'0.5px'}>
          {leftSection}
        </Center>
      )}

      {/*<Tooltip*/}
      {/*  label={name}*/}
      {/*  position={tooltipPosition}*/}
      {/*  openDelay={250}*/}
      {/*  disabled={!name || !tooltipPosition}*/}
      {/*>*/}
      <Text
        fw={fw}
        lineClamp={lineClamp}
        style={{
          cursor: editingDisabled ? 'inherit' : 'text',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          alignContent: 'center',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-all',
        }}
        c={name ? undefined : 'dimmed'}
        fz={fontSize}
        size={size}
        // mih={height}
        pt={textPaddingTop}
        lh={1.55}
        w={'100%'}
        h={'100%'}
        title={name}
      >
        {name || placeholder}
      </Text>
      {/*</Tooltip>*/}
    </Flex>
  );
}
